import React, { useState, useEffect } from "react";
import {
  Drawer,
  Form,
  Input,
  message,
  Button,
  Table,
  Typography,
  Col,
  Row,
  Tooltip,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import {
  UPDATE_ZONE,
  CREATE_SUB_ZONE,
  UPDATE_SUB_ZONE,
} from "./requests";
import { zoneTypes } from "../../../../types";

const { Title } = Typography;

interface EditZoneProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
  zone: typeof zoneTypes.zone | null;
}

const EditZone: React.FC<EditZoneProps> = ({
  showModal,
  setShowModal,
  refetch,
  zone,
}) => {
  const [form] = Form.useForm();
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
  const [newSubZoneName, setNewSubZoneName] = useState<string>("");
  const [subZones, setSubZones] = useState<typeof zoneTypes.subZone[]>([]);
  const [loadingCreateSubZone, setLoadingCreateSubZone] = useState<boolean>(false);
  const [editingSubZone, setEditingSubZone] = useState<typeof zoneTypes.subZone | null>(null);
  const [loadingUpdateSubZone, setLoadingUpdateSubZone] = useState<boolean>(false);

  const [updateZoneMutation] = useMutation(UPDATE_ZONE);
  const [createSubZoneMutation] = useMutation(CREATE_SUB_ZONE);
  const [updateSubZoneMutation] = useMutation(UPDATE_SUB_ZONE);

  useEffect(() => {
    if (zone) {
      const subZones = zone.subZones || [];
      form.setFieldsValue({
        name: zone.name,
      });

      setSubZones(subZones);
    }
  }, [zone, form, showModal]);

  const handleAddSubZone = async () => {
    setLoadingCreateSubZone(true);
    if (!newSubZoneName) {
      message.error("Por favor ingresa el nombre del municipio");
      return;
    }

    try {
      const { data } = await createSubZoneMutation({
        variables: {
          subZone: {
            name: newSubZoneName,
            zone: zone?.id,
          },
        },
      });

      setSubZones([
        ...subZones,
        { name: newSubZoneName,
          id: data.createSubZone.subZone.id },
      ]);
      setNewSubZoneName("");
      message.success("Municipio añadido exitosamente");
      await refetch();
    } catch (err) {
      console.error(err);
      message.error("Error al añadir municipio");
    } finally {
      setLoadingCreateSubZone(false);
    }
  };

  const handleEditSubZone = (subZone: typeof zoneTypes.subZone) => {
    setEditingSubZone(subZone);
    setNewSubZoneName(subZone.name);
  };

  const handleUpdateSubZone = async () => {
    setLoadingUpdateSubZone(true);
    if (!newSubZoneName) {
      message.error("Por favor ingresa el nombre del municipio");
      return;
    }

    try {
      await updateSubZoneMutation({
        variables: {
          id: editingSubZone!.id,
          subZone: {
            name: newSubZoneName,
            zone: zone!.id,
          },
        },
      });

      setSubZones(
        subZones.map((subZone) =>
          subZone.id === editingSubZone!.id
            ? { ...subZone,
              name: newSubZoneName }
            : subZone),
      );
      message.success("Municipio actualizado exitosamente");
      await refetch();
      setEditingSubZone(null);
      setNewSubZoneName("");
    } catch (err) {
      console.error(err);
      message.error("Error al actualizar municipio");
    } finally {
      setLoadingUpdateSubZone(false);
    }
  };

  const columns = [
    { title: "Nombre",
      dataIndex: "name",
      key: "name",
      width: "75%" },
    {
      title: "Acciones",
      key: "actions",
      width: "25%",
      render: (row: typeof zoneTypes.subZone) => (
        <>
          <Tooltip placement="top" title="Editar">
            <Button
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              style={{ margin: 5 }}
              onClick={() => handleEditSubZone(row)}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  const onFinish = async (values: { name: string }) => {
    setLoadingUpdate(true);
    try {
      const payload = {
        variables: {
          id: zone!.id,
          zone: {
            name: values.name || zone!.name,
          },
        },
      };

      await updateZoneMutation(payload);
      await refetch();
      message.success("Zona actualizada exitosamente");
    } catch (err) {
      console.error(err);
      message.error("Hubo un error al actualizar la zona");
    } finally {
      onCancel();
      setLoadingUpdate(false);
    }
  };

  const onCancel = () => {
    setShowModal(false);
    form.resetFields();
    setEditingSubZone(null);
    setNewSubZoneName("");
  };

  return (
    <Drawer
      open={showModal}
      title="Editar Zona"
      footer={null}
      onClose={onCancel}
      width={"50%"}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="name"
          label="Nombre"
          rules={[{ required: true,
            message: "Por favor ingresa el nombre" }]}
        >
          <Input placeholder="Ingrese el Nombre de la Zona" />
        </Form.Item>
        <Form.Item>
          <Button
            loading={loadingUpdate}
            size="large"
            block
            type="primary"
            htmlType="submit"
          >
            Actualizar Zona
          </Button>
        </Form.Item>
      </Form>

      <Title level={5}>
        {editingSubZone ? "Actualizar Municipio" : "Agregar Nuevo Municipio"}
      </Title>
      <Row>
        <Col span={20}>
          <Input
            placeholder="Nombre"
            value={newSubZoneName}
            onChange={(e) => setNewSubZoneName(e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Button
            type="primary"
            onClick={editingSubZone ? handleUpdateSubZone : handleAddSubZone}
            style={{ marginLeft: 10 }}
            disabled={!newSubZoneName}
            loading={loadingCreateSubZone || loadingUpdateSubZone}
          >
            {editingSubZone ? "Actualizar" : "Agregar"}
          </Button>
        </Col>
      </Row>

      <Table
        title={() => <Title level={4}>Municipios</Title>}
        rowKey="id"
        dataSource={subZones}
        columns={columns}
        pagination={false}
      />
    </Drawer>
  );
};

export default EditZone;
